import { useCallback, useContext, type VoidFunctionComponent } from 'react';
import ContentLoader from 'react-content-loader';
import { JSONTree } from 'react-json-tree';
import XMLViewer from 'react-xml-viewer';
import type { JsonObject } from 'type-fest';

import {
  AttributionTextReportPublicationStatuses,
  JSONReportPublicationStatuses,
  ReportPublicationStatusToString,
  TextReportPublicationStatuses,
  XMLReportPublicationStatuses,
} from '../../../../shared/model-typings/projectgrouprelease';
import { RouterLink } from '../../../components/generic';
import Loader from '../../../components/generic/Loader';
import PaginationFooter from '../../../components/generic/Pagination/PaginationFooter';
import Text from '../../../components/generic/Text';
import ReportViewer from '../../../components/ReportViewer';
import { RouterContext, useParams } from '../../../contexts/RouterContext';
import { useAsyncRequest, usePrevious } from '../../../hooks';
import { get } from '../../../redux/api';
import COLORS from '../../../theme/colors';
import { JSONTreeTheme } from '../../../theme/jsontree';
import { containEvent } from '../../../utils';
import { releaseGroupDependenciesRoute, releaseGroupReleaseRoute, releaseReportRoute } from '../../helpers/routes';
import NotFound from '../../NotFound';
import { usePagination, useViewType } from '../hooks';
import { fossaAnalysisViewType } from '../hooks/useViewType';
import type { ReleaseGroupRelease } from '../../../../shared/publicPortal/types';

import FossaAnalysisReport from './FossaAnalysisReport';
import type { ReleaseGroupParams } from './types';

import styles from './ReleaseGroup.module.less';

type ReportParams = {
  isLoading: boolean;
  release?: ReleaseGroupRelease;
};

const VersionTitle: VoidFunctionComponent<ReportParams> = ({ isLoading, release }) => {
  if (isLoading) {
    return (
      <ContentLoader
        speed={2}
        width={150}
        height={22}
        foregroundColor={COLORS.fossaGray05}
        backgroundColor={COLORS.fossaGray10}
      >
        <rect x="0" y="0" rx="3" ry="3" width="150" height="22" />
      </ContentLoader>
    );
  }

  if (!release) {
    return <Text.H4>Version unknown</Text.H4>;
  }

  return <Text.H4>Version {release.title}</Text.H4>;
};

const Report: VoidFunctionComponent<ReportParams> = ({ isLoading, release }) => {
  const { slug, releaseGroupId, releaseId } = useParams<ReleaseGroupParams>();
  const { page, count, setPage, setCount, totalItems, setTotalItems } = usePagination();
  const { replacePath } = useContext(RouterContext);
  const viewType = useViewType();
  const defaultViewRoute = releaseGroupReleaseRoute(slug, Number(releaseGroupId), Number(releaseId));
  const dependenciesRoute = releaseGroupDependenciesRoute(slug, Number(releaseGroupId), Number(releaseId));
  /**
   * When switching between releases this component will re-render at least
   * three times.
   * 1. When the `release` prop changes
   * 2. After the `useEffect` hooks run, causing us to fetch the report
   * 3. After the report data finishes loading
   *
   * Unfortunately, in between renders 1 and 2 the report data for the
   * previously visible report is still stored in the value attribute of
   * `useAsyncRequest` and the `isLoading` attribute is still false.
   * This can cause React to throw an error if the previous report was JSON and
   * the currently selected release would render plain text because React will
   * now be trying to render the JSON object as a React Element.
   *
   * The `usePrevious` hook allows us to track whether React's internal state
   * has been updated yet by storing the release id as a ref updating in an
   * effect. This mimics the internals of `useAsyncRequest` and allows us to
   * show a loading state during render 1 when the `release` prop has changed
   * but the `isLoading` value from `useAsyncRequest` has not yet updated.
   */
  const renderedReleaseId = usePrevious(release?.id);

  const getReleaseReport = useCallback(() => {
    if (!release) {
      return Promise.resolve({ data: undefined });
    }

    let responseType: 'json' | 'text' = 'text';
    if ((JSONReportPublicationStatuses as readonly any[]).includes(release.publishedOnPortal)) {
      responseType = 'json';
    }

    return get(releaseReportRoute(slug, release.id), true, undefined, { responseType });
  }, [release, slug]);
  const { value: reportData, isLoading: isLoadingReport } = useAsyncRequest(getReleaseReport);

  if (!isLoading && !release) {
    return (
      <div className={styles.report}>
        <NotFound />
      </div>
    );
  }

  const getReportElement = () => {
    if (isLoading || isLoadingReport || renderedReleaseId !== release?.id) {
      return <Loader />;
    }

    if ((JSONReportPublicationStatuses as readonly any[]).includes(release?.publishedOnPortal)) {
      return <JSONReport report={reportData as JsonObject} />;
    }
    if ((XMLReportPublicationStatuses as readonly any[]).includes(release?.publishedOnPortal)) {
      return <XMLReport report={reportData as string} />;
    }
    if ((TextReportPublicationStatuses as readonly any[]).includes(release?.publishedOnPortal)) {
      return <PlaintextReport report={reportData as string} />;
    }
    if ((AttributionTextReportPublicationStatuses as readonly any[]).includes(release?.publishedOnPortal)) {
      return <PlaintextReport report={reportData as string} />;
    }

    return undefined;
  };

  return (
    <div className={styles.reportContainer}>
      <div className={styles.report}>
        <div className={styles.reportHeader}>
          <VersionTitle isLoading={isLoading} release={release} />
        </div>
        <ReportViewer>
          <ReportViewer.Format>
            <span>View:</span>
            {release?.publishedOnPortal && (
              <RouterLink
                activeClassName={styles.selectedViewLink}
                className={styles.viewLink}
                to={defaultViewRoute}
                onClick={containEvent(() => {
                  replacePath(defaultViewRoute);
                })}
              >
                {ReportPublicationStatusToString[release.publishedOnPortal]}
              </RouterLink>
            )}
            <RouterLink
              activeClassName={styles.selectedViewLink}
              className={styles.viewLink}
              to={dependenciesRoute}
              onClick={containEvent(() => {
                replacePath(dependenciesRoute);
              })}
            >
              Dependencies
            </RouterLink>
          </ReportViewer.Format>
          <ReportViewer.Content>
            {viewType === fossaAnalysisViewType && release ? (
              <div className={styles.fossaAnalysisReportContent}>
                <FossaAnalysisReport release={release} setTotalItems={setTotalItems} page={page} count={count} />
              </div>
            ) : (
              <div className={styles.reportContent}>{getReportElement()}</div>
            )}
          </ReportViewer.Content>
        </ReportViewer>
      </div>
      {viewType === fossaAnalysisViewType && !!totalItems && (
        <PaginationFooter page={page} count={count} totalItems={totalItems} setPage={setPage} setCount={setCount} />
      )}
    </div>
  );
};

const JSONReport: VoidFunctionComponent<{ report: JsonObject }> = ({ report }) => (
  <JSONTree
    data={report}
    theme={JSONTreeTheme}
    shouldExpandNodeInitially={(_keyPath, _data, level) => level < 5}
    invertTheme
    hideRoot
  />
);

const XMLReport: VoidFunctionComponent<{ report: string }> = ({ report }) => (
  <XMLViewer xml={report} collapsible initalCollapsedDepth={4} />
);

const PlaintextReport: VoidFunctionComponent<{ report: string }> = ({ report }) => <pre>{report}</pre>;

export default Report;
